import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Camera, Upload, Send, X, ChevronLeft, ChevronRight, Sparkles, Loader2, User, ArrowRight, ArrowLeft } from "lucide-react";

const MAX_PHOTOS = 5;
const MAX_KB = 300;
const ALLOWED = ["image/jpeg", "image/jpg", "image/png"];

function PageHero() {
  return (
    <section className="relative overflow-hidden bg-gradient-to-r from-[#0D5C63] via-[#0D5C63] to-[#083E43] text-white">
      <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 90% 10%, #F26419 0, transparent 45%), radial-gradient(circle at 10% 90%, #D90368 0, transparent 45%)" }} />
      <div className="relative max-w-6xl mx-auto px-5 py-14">
        <div className="trust-eyebrow text-[10px] text-[#F26419] font-bold">Share Your Memories</div>
        <h1 className="trust-serif text-4xl sm:text-5xl mt-2 leading-tight">Your memories are a part of our <span className="text-[#F26419]">history</span>.</h1>
        <div className="mt-4 max-w-3xl text-white/85 leading-relaxed">
          <p>Every classroom, corridor, laboratory, playground and campus event carries a story. Over the years, thousands of students, teachers and staff members have contributed to the journey of GS Mandal and its MIT Group of Institutions.</p>
          <p className="mt-3">Alumni and members of the GS Mandal family are warmly invited to submit photographs and stories from their time on campus. Selected submissions will be published on the GS Mandal website after verification and approval by our website administrator.</p>
        </div>
      </div>
    </section>
  );
}

function SubmitForm({ onSubmitted }) {
  const [anon, setAnon] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [year, setYear] = useState("");
  const [people, setPeople] = useState("");
  const [photographer, setPhotographer] = useState("");
  const [instituteId, setInstituteId] = useState("");
  const [story, setStory] = useState("");
  const [institutes, setInstitutes] = useState([]);
  const [photos, setPhotos] = useState([]); // [{file, caption, preview}]
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/public/institutes");
        setInstitutes(Array.isArray(data) ? data : []);
      } catch { /* silent */ }
    })();
  }, []);

  const addPhotos = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (photos.length + incoming.length > MAX_PHOTOS) {
      toast.error(`You can attach at most ${MAX_PHOTOS} photographs`);
      return;
    }
    const additions = [];
    for (const f of incoming) {
      if (!ALLOWED.includes(f.type)) { toast.error(`${f.name}: only JPG or PNG allowed`); continue; }
      if (f.size > MAX_KB * 1024) { toast.error(`${f.name}: must be ${MAX_KB} KB or smaller`); continue; }
      additions.push({ file: f, caption: "", preview: URL.createObjectURL(f) });
    }
    setPhotos((cur) => [...cur, ...additions]);
  };

  const removePhoto = (idx) => setPhotos((cur) => cur.filter((_, i) => i !== idx));
  const setCaption = (idx, val) => setPhotos((cur) => cur.map((p, i) => i === idx ? { ...p, caption: val } : p));

  const submit = async () => {
    if (photos.length === 0) return toast.error("Please attach at least one photograph");
    if (!year.trim()) return toast.error("Approximate year is required");
    if (!anon) {
      if (!name.trim()) return toast.error("Please share your name (or mark as anonymous)");
      if (!email.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return toast.error("Please enter a valid email");
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("is_anonymous", anon ? "true" : "false");
      fd.append("submitter_name", anon ? "" : name.trim());
      fd.append("submitter_email", anon ? "" : email.trim());
      fd.append("submitter_phone", anon ? "" : phone.trim());
      fd.append("approx_year", year.trim());
      fd.append("people_in_photo", people.trim());
      fd.append("photographer_name", photographer.trim());
      fd.append("institute_id", instituteId || "");
      fd.append("story", story.trim());
      fd.append("captions", JSON.stringify(photos.map((p) => p.caption || "")));
      photos.forEach((p) => fd.append("photos", p.file, p.file.name));
      await api.post("/public/memories", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Thank you — your memory has been submitted for review");
      onSubmitted?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Submission failed. Please try again.");
    } finally { setSaving(false); }
  };

  const inputCls = "w-full bg-white border border-[#E9E2D2] rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D5C63]";

  return (
    <div className="bg-white border border-[#E9E2D2] rounded-2xl shadow-sm p-6 md:p-8" data-testid="memories-form">
      <div className="trust-eyebrow text-[10px] text-[#D90368] font-bold">Submit</div>
      <h2 className="trust-serif text-2xl text-[#1A1A1A] mt-1">Send us a memory</h2>

      <label className="flex items-center gap-2 mt-6 text-sm text-[#1A1A1A]">
        <input type="checkbox" checked={anon} onChange={(e) => setAnon(e.target.checked)} data-testid="memories-anon" className="h-4 w-4 accent-[#0D5C63]" />
        Submit anonymously (hide my contact details from the public view)
      </label>

      <div className={`mt-4 grid md:grid-cols-2 gap-4 ${anon ? "opacity-60" : ""}`}>
        <Field label={`Your Name${anon ? "" : " *"}`}>
          <input type="text" data-testid="memories-name" className={inputCls} value={name} disabled={anon} onChange={(e) => setName(e.target.value)} placeholder="Full name" maxLength={160} />
        </Field>
        <Field label={`Email${anon ? "" : " *"}`}>
          <input type="email" data-testid="memories-email" className={inputCls} value={email} disabled={anon} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" maxLength={200} />
        </Field>
        <Field label="Phone (optional)">
          <input type="tel" data-testid="memories-phone" className={inputCls} value={phone} disabled={anon} onChange={(e) => setPhone(e.target.value)} placeholder="e.g. +91 …" maxLength={40} />
        </Field>
        <Field label="Approximate Year of Photograph *">
          <input type="text" data-testid="memories-year" className={inputCls} value={year} onChange={(e) => setYear(e.target.value)} placeholder="e.g. 1998 or 1995-99" maxLength={40} />
        </Field>
        <Field label="Names of People in Photograph">
          <input type="text" data-testid="memories-people" className={inputCls} value={people} onChange={(e) => setPeople(e.target.value)} placeholder="Comma-separated names" maxLength={800} />
        </Field>
        <Field label="Photographer's Name (if known)">
          <input type="text" data-testid="memories-photographer" className={inputCls} value={photographer} onChange={(e) => setPhotographer(e.target.value)} placeholder="e.g. Prof. Kulkarni" maxLength={160} />
        </Field>
        <Field label="Institute (optional)">
          <select data-testid="memories-institute" className={inputCls} value={instituteId} onChange={(e) => setInstituteId(e.target.value)}>
            <option value="">— Select an Institute (optional) —</option>
            <option value="legacy:mit_aurangabad_old">Marathwada Institute of Technology, Chh. SambhajiNagar (Aurangabad) (OLD)</option>
            {institutes.map((i) => (
              <option key={i.id} value={i.id}>{i.name}{i.city ? `, ${i.city}` : ""}</option>
            ))}
          </select>
        </Field>
      </div>

      <div className="mt-4">
        <Field label="Write Your Story (optional)">
          <textarea data-testid="memories-story" className={`${inputCls} min-h-[140px] leading-relaxed`} value={story} onChange={(e) => setStory(e.target.value)} placeholder="Share the story behind these photographs — the people, the moment, what it meant to you…" maxLength={4000} rows={6} />
          <div className="text-[10px] text-[#8a8478] mt-1 text-right">{story.length}/4000</div>
        </Field>
      </div>

      <div className="mt-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold">Photographs *</div>
            <div className="text-xs text-[#5B5B5B]">JPG or PNG · max {MAX_KB} KB each · up to {MAX_PHOTOS} photos per submission</div>
          </div>
          <label className={`inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider px-4 py-2 rounded-lg cursor-pointer transition-colors ${photos.length >= MAX_PHOTOS ? "bg-[#E9E2D2] text-[#8a8478] cursor-not-allowed" : "bg-[#0D5C63] hover:bg-[#083E43] text-white"}`}>
            <Upload size={14} /> Choose Photos
            <input type="file" accept="image/jpeg,image/png" multiple hidden data-testid="memories-file-input" onChange={(e) => { addPhotos(e.target.files); e.target.value = ""; }} disabled={photos.length >= MAX_PHOTOS} />
          </label>
        </div>

        {photos.length > 0 && (
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {photos.map((p, i) => (
              <div key={i} className="border border-[#E9E2D2] rounded-xl p-3 bg-[#FAFAF7]" data-testid={`memories-photo-${i}`}>
                <div className="relative aspect-[4/3] rounded-lg overflow-hidden bg-slate-100">
                  <img src={p.preview} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
                  <button type="button" onClick={() => removePhoto(i)} className="absolute top-2 right-2 bg-white/95 hover:bg-white text-red-700 rounded-full p-1.5 shadow-sm" aria-label="Remove" data-testid={`memories-photo-remove-${i}`}>
                    <X size={14} />
                  </button>
                  <div className="absolute bottom-2 left-2 bg-white/95 text-[10px] font-bold text-[#1A1A1A] px-2 py-0.5 rounded">{Math.ceil(p.file.size / 1024)} KB</div>
                </div>
                <input type="text" data-testid={`memories-caption-${i}`} value={p.caption} onChange={(e) => setCaption(i, e.target.value)} maxLength={800} placeholder="Add a caption for this photograph" className="w-full mt-2 bg-white border border-[#E9E2D2] rounded px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#0D5C63]" />
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-8 flex flex-wrap items-center justify-between gap-3 border-t border-[#F0EBE0] pt-6">
        <div className="text-xs text-[#5B5B5B]">Submissions are reviewed by our website administrator before being published.</div>
        <button type="button" onClick={submit} disabled={saving} data-testid="memories-submit" className="inline-flex items-center gap-2 bg-gradient-to-r from-[#F26419] to-[#D90368] hover:from-[#D90368] hover:to-[#F26419] text-white text-sm font-bold uppercase tracking-wider px-6 py-3 rounded-full shadow-lg shadow-[#F26419]/25 disabled:opacity-60">
          {saving ? <><Loader2 size={14} className="animate-spin" /> Submitting…</> : <><Send size={14} /> Share My Memory</>}
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold mb-1.5">{label}</div>
      {children}
    </label>
  );
}

function MemoryCard({ m, onOpen }) {
  const cover = m.photos?.[0];
  return (
    <button type="button" onClick={onOpen} data-testid={`memory-card-${m.id}`} className="text-left bg-white border border-[#E9E2D2] rounded-xl overflow-hidden shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all group">
      <div className="aspect-[4/3] bg-slate-100 relative overflow-hidden">
        {cover?.url ? (
          <img src={cover.url} alt={cover.caption || ""} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#8a8478]"><Camera size={32} /></div>
        )}
        {m.photos?.length > 1 && <div className="absolute top-2 right-2 bg-black/70 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">+{m.photos.length - 1}</div>}
        <div className="absolute bottom-2 left-2 bg-[#F26419] text-white text-[10px] font-bold px-2 py-0.5 rounded">{m.approx_year || "—"}</div>
      </div>
      <div className="p-4">
        {cover?.caption && <div className="text-sm text-[#1A1A1A] leading-snug line-clamp-2">{cover.caption}</div>}
        {m.institute_name && <div className="mt-1 text-[11px] text-[#0D5C63] font-semibold">{m.institute_name}</div>}
        {m.story && <div className="mt-1.5 text-[11px] text-[#5B5B5B] line-clamp-3 italic">"{m.story}"</div>}
        <div className="mt-2 flex items-center justify-between text-[11px] text-[#5B5B5B]">
          <span className="inline-flex items-center gap-1"><User size={11} /> {m.is_anonymous ? "Anonymous" : (m.submitter_name || "GS Mandal Family")}</span>
          {m.photographer_name && <span className="italic">Photo: {m.photographer_name}</span>}
        </div>
      </div>
    </button>
  );
}

function MemoryLightbox({ mem, onClose }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => setIdx(0), [mem]);
  if (!mem) return null;
  const photos = mem.photos || [];
  const cur = photos[idx];
  const prev = () => setIdx((i) => (i - 1 + photos.length) % photos.length);
  const next = () => setIdx((i) => (i + 1) % photos.length);
  return (
    <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4" onClick={onClose} data-testid="memories-lightbox">
      <button type="button" onClick={onClose} className="absolute top-4 right-4 text-white p-2 hover:bg-white/10 rounded-full" aria-label="Close"><X /></button>
      {photos.length > 1 && (
        <>
          <button type="button" onClick={(e) => { e.stopPropagation(); prev(); }} className="absolute left-4 text-white p-2 hover:bg-white/10 rounded-full"><ChevronLeft /></button>
          <button type="button" onClick={(e) => { e.stopPropagation(); next(); }} className="absolute right-4 text-white p-2 hover:bg-white/10 rounded-full"><ChevronRight /></button>
        </>
      )}
      <div className="max-w-6xl w-full max-h-[90vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
        <div className="w-full flex items-center justify-center">
          {cur?.url && <img src={cur.url} alt={cur.caption || ""} className="max-h-[70vh] max-w-full object-contain" />}
        </div>
        <div className="mt-4 max-w-3xl w-full text-white text-center">
          {cur?.caption && <div className="text-base">{cur.caption}</div>}
          {mem.institute_name && <div className="mt-1 text-sm text-[#F26419] font-semibold">{mem.institute_name}</div>}
          {mem.story && <div className="mt-3 text-sm text-white/85 italic max-w-2xl mx-auto leading-relaxed whitespace-pre-wrap">{mem.story}</div>}
          <div className="mt-2 text-xs text-white/70 flex items-center justify-center gap-3 flex-wrap">
            <span><b className="text-[#F26419]">Year:</b> {mem.approx_year || "—"}</span>
            {mem.people_in_photo && <span><b className="text-[#F26419]">People:</b> {mem.people_in_photo}</span>}
            {mem.photographer_name && <span><b className="text-[#F26419]">Photographer:</b> {mem.photographer_name}</span>}
            <span>· {mem.is_anonymous ? "Submitted anonymously" : `Shared by ${mem.submitter_name || "GS Mandal Family"}`}</span>
          </div>
          {photos.length > 1 && <div className="mt-2 text-[11px] text-white/60">{idx + 1} / {photos.length}</div>}
        </div>
      </div>
    </div>
  );
}

// Parse the first 4-digit year (1900–2099) out of a free-form `approx_year`
// string like "1998", "1995-99", "Aug 2001". Returns null if none found.
function extractYear(s) {
  if (!s) return null;
  const m = String(s).match(/(19|20)\d{2}/);
  if (!m) return null;
  const y = parseInt(m[0], 10);
  if (isNaN(y) || y < 1900 || y > 2099) return null;
  return y;
}

function bucketByDecade(list = []) {
  const buckets = new Map(); // decadeStart -> items[]
  const undated = [];
  for (const m of list) {
    const y = extractYear(m.approx_year);
    if (y == null) { undated.push(m); continue; }
    const start = y - (y % 10);
    if (!buckets.has(start)) buckets.set(start, []);
    buckets.get(start).push(m);
  }
  // Ascending by decade (oldest first)
  const decades = Array.from(buckets.entries()).sort((a, b) => a[0] - b[0]);
  return { decades, undated };
}

export default function TrustMemories() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/public/memories");
      setList(data || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const { decades, undated } = useMemo(() => bucketByDecade(list), [list]);

  return (
    <div data-testid="trust-memories-page">
      <PageHero />

      {/* Prominent CTA — routes to the submission page */}
      <section className="max-w-6xl mx-auto px-5 pt-10">
        <div className="bg-gradient-to-r from-[#0D5C63] to-[#083E43] rounded-2xl p-6 md:p-8 shadow-lg text-white flex flex-col md:flex-row items-start md:items-center gap-5 md:gap-8" data-testid="memories-submit-cta">
          <div className="flex-1">
            <div className="trust-eyebrow text-[10px] text-[#F26419] font-bold">Alumni Corner</div>
            <h2 className="trust-serif text-2xl sm:text-3xl mt-1 leading-snug">Do you have an old photograph or story?</h2>
            <p className="mt-2 text-white/85 text-sm leading-relaxed max-w-2xl">Alumni and members of the GS Mandal family — help us preserve our shared history. Submit your photographs and stories; selected memories will appear on this page after review.</p>
          </div>
          <Link to="submit" data-testid="memories-open-submit" className="inline-flex items-center gap-2 bg-gradient-to-r from-[#F26419] to-[#D90368] hover:from-[#D90368] hover:to-[#F26419] text-white text-sm font-bold uppercase tracking-wider px-6 py-3.5 rounded-full shadow-lg shadow-[#F26419]/25 whitespace-nowrap transition-all hover:-translate-y-0.5">
            <Camera size={15} /> Share Your Memory <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* Decade-wise gallery */}
      <section className="bg-[#FAFAF7] border-t border-[#E9E2D2] mt-14 py-14" data-testid="trust-memories-gallery">
        <div className="max-w-6xl mx-auto px-5">
          <div className="text-center max-w-3xl mx-auto">
            <div className="trust-eyebrow text-[10px] text-[#D90368] font-bold">Approved Memories · Decade Wise</div>
            <h2 className="trust-serif text-3xl sm:text-4xl text-[#1A1A1A] mt-2">Stories from the GS Mandal <span className="grad-text">Family</span></h2>
            <div className="gold-rule-solid mx-auto mt-3" />
          </div>
          <div className="mt-10">
            {loading ? (
              <div className="text-sm text-[#5B5B5B]">Loading memories…</div>
            ) : list.length === 0 ? (
              <div className="bg-white border-2 border-dashed border-[#E9E2D2] rounded-2xl p-12 text-center">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-[#F26419]/15 to-[#D90368]/15 text-[#0D5C63]">
                  <Sparkles size={22} />
                </div>
                <div className="trust-serif text-lg text-[#1A1A1A] mt-4">Be the first to share a memory</div>
                <p className="text-sm text-[#5B5B5B] mt-1 max-w-md mx-auto">Approved submissions will appear here once the administrator publishes them.</p>
                <Link to="submit" data-testid="memories-empty-submit" className="inline-flex items-center gap-2 mt-5 bg-gradient-to-r from-[#F26419] to-[#D90368] text-white text-xs font-bold uppercase tracking-wider px-5 py-2.5 rounded-full shadow-md">
                  <Camera size={13} /> Share Your Memory
                </Link>
              </div>
            ) : (
              <div className="space-y-14">
                {decades.map(([start, items]) => (
                  <div key={start} data-testid={`memories-decade-${start}`}>
                    <div className="flex items-center gap-3 mb-5">
                      <div className="text-[10px] tracking-[0.25em] uppercase text-[#0D5C63] font-bold">Decade</div>
                      <div className="trust-serif text-2xl sm:text-3xl text-[#1A1A1A]">{start}–{start + 10}</div>
                      <div className="flex-1 h-px bg-[#E9E2D2]" />
                      <div className="text-xs text-[#5B5B5B]">{items.length} memor{items.length === 1 ? "y" : "ies"}</div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {items.map((m) => <MemoryCard key={m.id} m={m} onOpen={() => setOpen(m)} />)}
                    </div>
                  </div>
                ))}
                {undated.length > 0 && (
                  <div data-testid="memories-decade-undated">
                    <div className="flex items-center gap-3 mb-5">
                      <div className="text-[10px] tracking-[0.25em] uppercase text-[#0D5C63] font-bold">Year</div>
                      <div className="trust-serif text-2xl sm:text-3xl text-[#1A1A1A]">Undated</div>
                      <div className="flex-1 h-px bg-[#E9E2D2]" />
                      <div className="text-xs text-[#5B5B5B]">{undated.length} memor{undated.length === 1 ? "y" : "ies"}</div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {undated.map((m) => <MemoryCard key={m.id} m={m} onOpen={() => setOpen(m)} />)}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      <MemoryLightbox mem={open} onClose={() => setOpen(null)} />
    </div>
  );
}

// Standalone submission page — routed at `/memories/submit`. Renders the hero
// plus the full SubmitForm on its own page so the gallery view stays uncluttered.
export function TrustMemoriesSubmit() {
  const navigate = useNavigate();
  return (
    <div data-testid="trust-memories-submit-page">
      <PageHero />
      <section className="max-w-6xl mx-auto px-5 py-14">
        <div className="mb-6">
          <Link to=".." relative="path" data-testid="memories-back-to-gallery" className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-[#0D5C63] hover:text-[#F26419]">
            <ArrowLeft size={13} /> Back to Memories Gallery
          </Link>
        </div>
        <SubmitForm onSubmitted={() => navigate("/memories")} />
      </section>
    </div>
  );
}
